import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, MapPin, Store, Flashlight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TorchesPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSuppliers, setActiveSuppliers] = useState(null);
  const [tryBeforeYouBuyEnabled, setTryBeforeYouBuyEnabled] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProducts();
    axios.get(`${API_URL}/suppliers`)
      .then((res) => setActiveSuppliers(new Set((res.data || []).map((s) => (s.name || '').toLowerCase()))))
      .catch(() => setActiveSuppliers(new Set()));
    axios.get(`${API_URL}/site-content`)
      .then((res) => {
        const v = res.data?.try_before_you_buy_enabled;
        setTryBeforeYouBuyEnabled(String(v ?? 'true').toLowerCase() !== 'false');
      })
      .catch(() => setTryBeforeYouBuyEnabled(true));
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/products?category=torches`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load torches');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(`${product.name} added to cart`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A2342]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 bg-[#FAFAFA]" data-testid="torches-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Flashlight className="w-10 h-10 text-[#FF6B35]" />
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#0A2342]">
              Dive Torches
            </h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl">
            Illuminate your underwater adventures. From compact backup lights to powerful primary torches for night diving and exploration.
          </p>
        </div>

        {/* Store Location Notice — only shown when Aqua Divers supplier is active AND admin hasn't hidden the panel */}
        {tryBeforeYouBuyEnabled && activeSuppliers && activeSuppliers.has('aqua divers') && (
        <div className="bg-[#0A2342] text-white p-6 rounded-lg mb-8">
          <div className="flex items-start gap-3 mb-4">
            <Store className="w-6 h-6 text-[#FF6B35] flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-lg mb-2">Try Before You Buy</h3>
              <p className="text-gray-300 text-sm">All dive torches can be tested at Aqua Divers:</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-9">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[#FF6B35] flex-shrink-0 mt-1" />
              <div className="text-sm">
                <p className="font-semibold">Pretoria</p>
                <p className="text-gray-300">Shop G145 Parkview Shopping Centre,</p>
                <p className="text-gray-300">24 Garsfontein Rd, Pretoria</p>
                <p className="text-[#FF6B35] mt-1">+2712 880 5300</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[#FF6B35] flex-shrink-0 mt-1" />
              <div className="text-sm">
                <p className="font-semibold">Randburg</p>
                <p className="text-gray-300">Unit C5, Northland Deco Park,</p>
                <p className="text-gray-300">Design Blvd, Hoogland, Randburg</p>
                <p className="text-[#FF6B35] mt-1">+2711 794 9444</p>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Products Grid */}
        {products.length === 0 ? (
          <div className="text-center py-16">
            <Flashlight className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-xl text-gray-500">No torches available at the moment</p>
            <p className="text-gray-400 mt-2">Check back soon for new arrivals</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-white border border-gray-100 rounded-lg overflow-hidden hover:border-[#0A2342]/20 hover:shadow-xl transition-all duration-300 group"
                data-testid={`torch-card-${product.id}`}
              >
                <Link to={`/products/${product.id}`}>
                  <div className="aspect-square overflow-hidden bg-gray-100 flex items-center justify-center">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </Link>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <Link to={`/products/${product.id}`}>
                      <h3 className="text-xl font-heading font-semibold text-[#0A2342] group-hover:text-[#FF6B35] transition-colors">
                        {product.name}
                      </h3>
                    </Link>
                    {product.in_stock ? (
                      <Badge className="bg-green-100 text-green-800 border-green-200">In Stock</Badge>
                    ) : (
                      <Badge variant="destructive">Out of Stock</Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                    {product.description}
                  </p>
                  
                  <div className="mb-4">
                    <div className="text-2xl font-mono-accent font-bold text-[#0A2342] mb-2">
                      R{product.price.toFixed(2)}
                    </div>
                    <div className="text-xs space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] border-[#0A2342] text-[#0A2342]">Club 20%</Badge>
                        <span className="font-mono-accent font-semibold text-[#0A2342]">
                          R{(product.price * 0.8).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] border-[#FF6B35] text-[#FF6B35]">Pro 25%</Badge>
                        <span className="font-mono-accent font-semibold text-[#FF6B35]">
                          R{(product.price * 0.75).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleAddToCart(product)}
                    disabled={!product.in_stock}
                    className="w-full bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 shadow-lg hover:shadow-xl transition-all duration-300"
                    data-testid={`add-to-cart-${product.id}`}
                  >
                    Add to Cart
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TorchesPage;
